import React, { useCallback, useEffect, useMemo, useState } from "react";
import { sendSignInLinkToEmail, signOut } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  doc,
  increment,
  limit,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, db, functions } from "../firebase";
import { useAccount } from "../context/AccountContext";
import {
  invalidateFirestoreReadCache,
  invalidateFirestoreReadCacheByPrefix,
  trackedGetDoc,
  trackedGetDocs,
} from "../utils/firestoreReadCache";
import {
  FEATURE_KEYS,
  FEATURE_LABELS,
  getFeatureAccess,
  normalizeAccountStatus,
  normalizeBillingPlan,
  normalizeFeatureMap,
  normalizePlan,
  resolveAccessPlan,
} from "../utils/featureAccess";
import {
  DEFAULT_STAFF_LIMIT,
  normalizeExtraStaffPurchased,
  normalizeStaffLimit,
} from "../utils/teamAccess";
import { IS_DEV_UI_ENABLED } from "../utils/devUi";

const STATUS_OPTIONS = ["active", "inactive"];
const ACCOUNT_STATUS_OPTIONS = ["trial", "trial_exhausted", "payment_pending", "active", "suspended"];
const BILLING_PLAN_OPTIONS = ["trial", "basic", "standard", "premium", "custom"];
const ROLE_OPTIONS = ["owner", "admin", "staff", "viewer"];
const STAFF_SEAT_ROLES = new Set(["admin", "staff", "viewer"]);
const isDevRuntime = IS_DEV_UI_ENABLED;
const PRODUCTION_INVITE_ORIGIN = "https://quote.fabriquote.in";
const STAFF_LIMIT_MESSAGE = "You have reached your staff limit.";
const PLAN_CARDS = [
  {
    code: "general",
    label: "General",
    description: "Dashboard, CRM, General Quotes, Saved Quotes, Settings",
  },
  {
    code: "curtain",
    label: "Curtain",
    description: "Curtain Quotes, Stitching & Rates, Optimizer, Customer PDF, Workshop PDF",
  },
  {
    code: "full",
    label: "Full",
    description: "Every FabriQuote module",
  },
];

const cardStyle = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 22,
  boxShadow: "0 18px 50px rgba(15,23,42,0.08)",
};

const inputStyle = {
  width: "100%",
  border: "1px solid #cbd5e1",
  borderRadius: 12,
  padding: "11px 12px",
  fontSize: 14,
  outline: "none",
  background: "#ffffff",
};

function normalizeEmail(value = "") {
  return String(value || "").trim().toLowerCase();
}

function emailInviteKey(value = "") {
  return normalizeEmail(value).replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

function getInviteExpiryTimestamp() {
  return Timestamp.fromDate(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000));
}

function isValidEmail(value = "") {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeEmail(value));
}

function getInviteOrigin() {
  if (typeof window === "undefined") return PRODUCTION_INVITE_ORIGIN;
  const origin = window.location?.origin || "";
  const hostname = window.location?.hostname || "";
  const isLocalhost =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1";
  return isDevRuntime && isLocalhost && origin ? origin : PRODUCTION_INVITE_ORIGIN;
}

function getInviteEmailErrorMessage(error) {
  const code = String(error?.code || "").toLowerCase();
  if (code.includes("invalid-email")) return "Please enter a valid email address.";
  if (code.includes("unauthorized-continue-uri")) {
    return "Invite link domain is not authorized in Firebase.";
  }
  if (code.includes("operation-not-allowed")) {
    return "Email link sign-in is not enabled in Firebase Auth.";
  }
  if (code.includes("too-many-requests")) {
    return "Too many invite attempts. Please try again later.";
  }
  if (code.includes("network-request-failed")) {
    return "Network issue while sending invite email.";
  }
  if (code.includes("permission-denied")) {
    return "Invite email sent, but updating the invite record was blocked.";
  }
  return "Could not send invite email.";
}

function getSuperAdminCallableErrorMessage(error, fallback = "Could not complete this admin action.") {
  const code = String(error?.code || "").toLowerCase();
  const message = String(error?.message || "");
  if (code.includes("permission-denied")) return "Not authorized to perform this Super Admin action.";
  if (code.includes("unauthenticated")) return "Please sign in again before using Super Admin tools.";
  if (code.includes("invalid-argument")) return message || "Please check the form values.";
  if (code.includes("not-found")) return message || "The requested account or user was not found.";
  if (code.includes("failed-precondition")) return message || "This action cannot be completed yet.";
  if (message) return message;
  return fallback;
}

function validateInviteActionCodeSettings({ email, accountId, inviteId, actionCodeSettings }) {
  if (!isValidEmail(email)) throw new Error("Please enter a valid email address.");
  if (!String(accountId || "").trim()) throw new Error("Invite account id is missing.");
  if (!String(inviteId || "").trim()) throw new Error("Invite id is missing.");
  const url = String(actionCodeSettings?.url || "");
  if (!url || url.includes("undefined") || url.includes("null")) {
    throw new Error("Invite link is invalid.");
  }
  if (!isDevRuntime && !url.startsWith("https://")) {
    throw new Error("Invite link must use HTTPS in production.");
  }
}

function isStaffSeatRole(role = "staff") {
  return STAFF_SEAT_ROLES.has(String(role || "staff").trim().toLowerCase());
}

function isPendingInvite(invite = {}) {
  return ["pending", "pending_email_failed"].includes(
    String(invite.status || "pending").toLowerCase()
  );
}

function getStaffSeatUsage(members = [], invites = [], staffLimit = DEFAULT_STAFF_LIMIT, exclude = {}) {
  const normalizedLimit = normalizeStaffLimit(staffLimit);
  const excludeEmail = normalizeEmail(exclude.email || "");
  const excludeUid = String(exclude.uid || "").trim();
  const activeStaffMembers = members.filter((member) => {
    const role = String(member.role || "owner").trim().toLowerCase();
    const memberEmail = normalizeEmail(member.email || "");
    const memberUid = String(member.uid || "").trim();
    if (!isStaffSeatRole(role)) return false;
    if (member.isActive === false) return false;
    if (excludeUid && memberUid === excludeUid) return false;
    if (excludeEmail && memberEmail === excludeEmail) return false;
    return true;
  });
  const activeEmails = new Set(activeStaffMembers.map((member) => normalizeEmail(member.email || "")).filter(Boolean));
  const pendingStaffInvites = invites.filter((invite) => {
    const role = String(invite.role || "staff").trim().toLowerCase();
    const inviteEmail = normalizeEmail(invite.email || "");
    if (!isStaffSeatRole(role)) return false;
    if (!isPendingInvite(invite)) return false;
    if (excludeEmail && inviteEmail === excludeEmail) return false;
    if (inviteEmail && activeEmails.has(inviteEmail)) return false;
    return true;
  });

  return {
    active: activeStaffMembers.length,
    pending: pendingStaffInvites.length,
    total: activeStaffMembers.length + pendingStaffInvites.length,
    limit: normalizedLimit,
    remaining: Math.max(0, normalizedLimit - activeStaffMembers.length - pendingStaffInvites.length),
    overLimit: activeStaffMembers.length + pendingStaffInvites.length > normalizedLimit,
  };
}

function normalizeAccountDoc(id, data = {}) {
  const billingPlan = normalizeBillingPlan(data.billingPlan || data.subscriptionPlan || data.plan || "trial");
  const plan = resolveAccessPlan(
    billingPlan,
    data.accessPlan || data.featurePlan || data.planCode || ""
  );
  const accountStatus = normalizeAccountStatus(
    data.accountStatus || data.subscriptionStatus || data.status || "",
    data.isActive !== false
  );
  const extraStaffPurchased = normalizeExtraStaffPurchased(data.extraStaffPurchased);
  const hasStaffLimit = data.staffLimit != null;
  const staffLimit = normalizeStaffLimit(
    hasStaffLimit ? data.staffLimit : DEFAULT_STAFF_LIMIT + extraStaffPurchased
  );
  return {
    id,
    name: String(data.name || "Untitled account"),
    ownerUid: String(data.ownerUid || ""),
    dataOwnerUid: String(data.dataOwnerUid || data.workspaceOwnerUid || data.ownerUid || ""),
    plan,
    billingPlan,
    accountStatus,
    isActive:
      data.isActive != null
        ? data.isActive === true
        : String(data.subscriptionStatus || "active") === "active",
    features: normalizeFeatureMap(data.features || data.featureFlags, plan),
    trialQuotationLimit: Number(data.trialQuotationLimit || 0) || 0,
    trialQuotationsUsed: Number(data.trialQuotationsUsed || 0) || 0,
    createdVia: String(data.createdVia || ""),
    manualPaymentNotes: String(data.manualPaymentNotes || ""),
    subscriptionValidUntil: data.subscriptionValidUntil || null,
    lockedReason: String(data.lockedReason || ""),
    staffLimit,
    activeStaffCount: normalizeStaffLimit(data.activeStaffCount, 0),
    storedActiveStaffCount: normalizeStaffLimit(data.activeStaffCount, 0),
    extraStaffPurchased,
    staffLimitNeedsMigration: !hasStaffLimit,
    extraStaffPurchasedNeedsMigration: data.extraStaffPurchased == null,
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
  };
}

function invalidateAccountAccessCaches(accountId = "") {
  invalidateFirestoreReadCache("accounts");
  invalidateFirestoreReadCache("pendingAccountInvites?status=pending&limit=1000");
  invalidateFirestoreReadCache("pendingAccountInvites?limit=1000");
  if (!accountId) return;
  invalidateFirestoreReadCacheByPrefix(`accounts/${accountId}`);
}

export default function SuperAdminPanel() {
  const { isSuperAdmin, loading, currentUser, refreshAccount } = useAccount();
  const [accounts, setAccounts] = useState([]);
  const [membersByAccount, setMembersByAccount] = useState({});
  const [pendingInvitesByAccount, setPendingInvitesByAccount] = useState({});
  const [activationRequests, setActivationRequests] = useState([]);
  const [websiteLeads, setWebsiteLeads] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [draft, setDraft] = useState(null);
  const [createDraft, setCreateDraft] = useState({
    name: "",
    plan: "full",
    adminEmail: "",
    isActive: true,
    staffLimit: DEFAULT_STAFF_LIMIT,
    extraStaffPurchased: 0,
  });
  const [memberDraft, setMemberDraft] = useState({
    name: "",
    uid: "",
    email: "",
    role: "staff",
    plan: "full",
    isActive: true,
  });
  const [editingMember, setEditingMember] = useState(null);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [resendingInviteId, setResendingInviteId] = useState("");
  const [testInviteEmailValue, setTestInviteEmailValue] = useState("");
  const [isTestingInviteEmail, setIsTestingInviteEmail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [lastSetupLink, setLastSetupLink] = useState(null);
  const [linkExistingDraft, setLinkExistingDraft] = useState({
    name: "",
    email: "",
    role: "admin",
    plan: "full",
    isActive: true,
  });
  const [linkingExistingUser, setLinkingExistingUser] = useState(false);
  const [sendingLeadTestEmail, setSendingLeadTestEmail] = useState(false);
  const [resendingLeadId, setResendingLeadId] = useState("");

  const loadAccounts = useCallback(async () => {
    setError("");
    try {
      // Super admins must see every customer workspace. Avoid orderBy here because
      // Firestore excludes documents that do not contain the ordered field.
      const snap = await trackedGetDocs(collection(db, "accounts"), {
        label: "superAdminAccounts",
        cacheKey: "accounts",
      });
      let nextAccounts = snap.docs
        .map((item) => normalizeAccountDoc(item.id, item.data()))
        .sort((a, b) => {
          const aTime = a.updatedAt?.seconds || a.createdAt?.seconds || 0;
          const bTime = b.updatedAt?.seconds || b.createdAt?.seconds || 0;
          return bTime - aTime;
        });

      const nextMembers = {};
      await Promise.all(
        nextAccounts.map(async (account) => {
          const memberSnap = await trackedGetDocs(
            collection(db, "accounts", account.id, "members"),
            {
              label: "superAdminAccountMembers",
              cacheKey: `accounts/${account.id}/members`,
            }
          );
          nextMembers[account.id] = memberSnap.docs.map((member) => ({
            uid: member.id,
            ...(member.data() || {}),
          }));
        })
      );
      setMembersByAccount(nextMembers);

      nextAccounts = nextAccounts.map((account) => {
        const activeStaffCount = getStaffSeatUsage(
          nextMembers[account.id] || [],
          [],
          account.staffLimit
        ).active;
        return {
          ...account,
          activeStaffCount,
        };
      });
      setAccounts(nextAccounts);
      if (!selectedId && nextAccounts[0]?.id) {
        setSelectedId(nextAccounts[0].id);
      }

      await Promise.all(
        nextAccounts
          .filter(
            (account) =>
              account.staffLimitNeedsMigration ||
              account.extraStaffPurchasedNeedsMigration ||
              account.activeStaffCount !== account.storedActiveStaffCount
          )
          .map((account) =>
            updateDoc(doc(db, "accounts", account.id), {
              staffLimit: account.staffLimit,
              activeStaffCount: account.activeStaffCount,
              extraStaffPurchased: account.extraStaffPurchased,
              updatedAt: serverTimestamp(),
            }).catch((migrationError) => {
              if (isDevRuntime) {
                console.warn("SUPERADMIN STAFF LIMIT MIGRATION SKIPPED", {
                  accountId: account.id,
                  code: migrationError?.code,
                  message: migrationError?.message,
                });
              }
            })
          )
      );

      const pendingSnap = await trackedGetDocs(
        query(collection(db, "pendingAccountInvites"), limit(1000)),
        {
          label: "superAdminPendingAccountInvites",
          cacheKey: "pendingAccountInvites?limit=1000",
        }
      );
      const nextPending = {};
      pendingSnap.docs.forEach((inviteDoc) => {
        const invite = { id: inviteDoc.id, ...(inviteDoc.data() || {}) };
        const accountId = String(invite.accountId || "");
        if (!accountId) return;
        if (!isPendingInvite(invite)) return;
        const activeMemberWithEmail = (nextMembers[accountId] || []).some(
          (member) =>
            member.isActive !== false &&
            normalizeEmail(member.email || "") === normalizeEmail(invite.email || "")
        );
        if (activeMemberWithEmail) return;
        nextPending[accountId] = [...(nextPending[accountId] || []), invite];
      });
      setPendingInvitesByAccount(nextPending);
    } catch (loadError) {
      setError(loadError?.message || "Could not load accounts.");
    }
  }, [selectedId]);

  const loadActivationRequests = useCallback(async () => {
    try {
      const requestsSnap = await trackedGetDocs(
        query(collection(db, "sales_activation_requests"), limit(100)),
        {
          label: "superAdminActivationRequests",
          cacheKey: "sales_activation_requests?limit=100",
        }
      );
      const nextRequests = requestsSnap.docs
        .map((item) => ({ id: item.id, ...(item.data() || {}) }))
        .sort((a, b) => {
          const aTime = a.updatedAt?.seconds || a.createdAt?.seconds || 0;
          const bTime = b.updatedAt?.seconds || b.createdAt?.seconds || 0;
          return bTime - aTime;
        });
      setActivationRequests(nextRequests);
    } catch (requestError) {
      if (isDevRuntime) {
        console.error("SUPERADMIN LOAD ACTIVATION REQUESTS FAILED", requestError);
      }
    }
  }, []);

  const loadWebsiteLeads = useCallback(async () => {
    try {
      const leadsSnap = await trackedGetDocs(
        query(collection(db, "website_leads"), limit(100)),
        {
          label: "superAdminWebsiteLeads",
          cacheKey: "website_leads?limit=100",
        }
      );
      const nextLeads = leadsSnap.docs
        .map((item) => ({ id: item.id, ...(item.data() || {}) }))
        .sort((a, b) => {
          const aTime = a.updatedAt?.seconds || a.createdAt?.seconds || 0;
          const bTime = b.updatedAt?.seconds || b.createdAt?.seconds || 0;
          return bTime - aTime;
        });
      setWebsiteLeads(nextLeads);
    } catch (leadError) {
      if (isDevRuntime) {
        console.error("SUPERADMIN LOAD WEBSITE LEADS FAILED", leadError);
      }
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      loadAccounts();
      loadActivationRequests();
      loadWebsiteLeads();
    }
  }, [isSuperAdmin, loadAccounts, loadActivationRequests, loadWebsiteLeads]);

  const filteredAccounts = useMemo(() => {
    const term = search.trim().toLowerCase();
    return accounts.filter((account) => {
      const matchesSearch =
        !term ||
        account.name.toLowerCase().includes(term) ||
        account.id.toLowerCase().includes(term) ||
        account.plan.toLowerCase().includes(term);
      const matchesPlan = planFilter === "all" || account.plan === planFilter;
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" ? account.isActive : !account.isActive);
      return matchesSearch && matchesPlan && matchesStatus;
    });
  }, [accounts, planFilter, search, statusFilter]);

  const selectedAccount = accounts.find((account) => account.id === selectedId) || null;
  const selectedStaffSeatUsage = useMemo(
    () =>
      selectedAccount
        ? getStaffSeatUsage(
            membersByAccount[selectedAccount.id] || [],
            pendingInvitesByAccount[selectedAccount.id] || [],
            selectedAccount.staffLimit
          )
        : {
            active: 0,
            pending: 0,
            total: 0,
            limit: DEFAULT_STAFF_LIMIT,
            remaining: DEFAULT_STAFF_LIMIT,
            overLimit: false,
          },
    [membersByAccount, pendingInvitesByAccount, selectedAccount]
  );

  useEffect(() => {
    if (selectedAccount) {
      setDraft({
        name: selectedAccount.name,
        ownerUid: selectedAccount.ownerUid,
        dataOwnerUid: selectedAccount.dataOwnerUid,
        plan: selectedAccount.plan,
        billingPlan: selectedAccount.billingPlan || "trial",
        accountStatus: selectedAccount.accountStatus || "active",
        isActive: selectedAccount.isActive,
        features: { ...selectedAccount.features },
        trialQuotationLimit: selectedAccount.trialQuotationLimit || 0,
        trialQuotationsUsed: selectedAccount.trialQuotationsUsed || 0,
        subscriptionValidUntil: selectedAccount.subscriptionValidUntil || "",
        manualPaymentNotes: selectedAccount.manualPaymentNotes || "",
        lockedReason: selectedAccount.lockedReason || "",
        staffLimit: selectedAccount.staffLimit,
        extraStaffPurchased: selectedAccount.extraStaffPurchased,
      });
      setLinkExistingDraft((prev) => ({
        ...prev,
        role: prev.role || "admin",
        plan: selectedAccount.plan || "full",
        isActive: true,
      }));
    }
  }, [selectedAccount]);

  if (loading) {
    return <FullScreenMessage title="Loading admin access" text="Checking your FabriQuote permissions..." />;
  }

  if (!currentUser) {
    return <FullScreenMessage title="Sign in required" text="Please sign in before opening the Super Admin panel." />;
  }

  if (!isSuperAdmin) {
    return (
      <FullScreenMessage
        title="Super Admin only"
        text="This panel is locked to platform owner accounts listed in /superAdmins/{uid}."
      />
    );
  }

  const updateDraftPlan = (plan) => {
    setDraft((prev) => ({
      ...prev,
      plan,
      features: getFeatureAccess(plan),
    }));
  };

  const findUserByEmail = async (email) => {
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) return null;

    const usersSnap = await trackedGetDocs(
      query(collection(db, "users"), where("email", "==", normalizedEmail), limit(1)),
      {
        label: "superAdminFindUserByNormalizedEmail",
        cacheKey: `users?email=${normalizedEmail}&limit=1`,
      }
    );

    if (!usersSnap.empty) {
      const userDoc = usersSnap.docs[0];
      return {
        uid: userDoc.id,
        ...(userDoc.data() || {}),
      };
    }

    const displayEmailSnap = await trackedGetDocs(
      query(collection(db, "users"), where("email", "==", email.trim()), limit(1)),
      {
        label: "superAdminFindUserByDisplayEmail",
        cacheKey: `users?email=${email.trim()}&limit=1`,
      }
    );

    if (!displayEmailSnap.empty) {
      const userDoc = displayEmailSnap.docs[0];
      return {
        uid: userDoc.id,
        ...(userDoc.data() || {}),
      };
    }

    return null;
  };

  const createAccountInviteActionCodeSettings = (email, accountId = "", inviteId = "") => {
    const origin = getInviteOrigin();
    const params = new URLSearchParams({
      accountInvite: "1",
      email,
    });
    if (accountId) {
      params.set("accountId", accountId);
      params.set("account", accountId);
    }
    if (inviteId) {
      params.set("inviteId", inviteId);
      params.set("invite", inviteId);
    }

    return {
      url: `${origin}/accept-invite?${params.toString()}`,
      handleCodeInApp: true,
    };
  };

  const sendAccountInviteEmail = async (email, actionCodeSettings, details = {}) => {
    const accountId = String(details.accountId || details.workspaceUid || "");
    const inviteId = String(details.inviteId || details.invite?.inviteId || details.invite?.id || "");
    validateInviteActionCodeSettings({
      email,
      accountId,
      inviteId,
      actionCodeSettings,
    });

    if (isDevRuntime) {
      console.log("TEAM INVITE EMAIL START", {
        email,
        accountId,
        workspaceUid: details.workspaceUid || accountId,
        actionCodeSettings,
        invite: details.invite || null,
      });
      console.log("TEAM INVITE EMAIL FUNCTION CALLED", {
        email,
        accountId,
        workspaceUid: details.workspaceUid || accountId,
        actionCodeSettings,
        invite: details.invite || null,
        ...details,
      });
    }

    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      if (isDevRuntime) {
        console.log("TEAM INVITE EMAIL SUCCESS", {
          email,
          ...details,
        });
      }
    } catch (emailError) {
      if (isDevRuntime) {
        console.error("TEAM INVITE EMAIL FAILED EXACT", {
          code: emailError?.code,
          message: emailError?.message,
          stack: emailError?.stack,
          invite: details.invite || null,
          email,
          accountId,
          workspaceUid: details.workspaceUid || accountId,
          actionCodeSettings,
          ...details,
        });
      }
      throw emailError;
    }
  };

  const logAddUserFailure = (error, details = {}) => {
    if (!isDevRuntime) return;
    console.error("SUPERADMIN ADD USER FAILED EXACT", {
      code: error?.code,
      message: error?.message,
      stack: error?.stack,
      ...details,
    });
  };

  const createPendingMemberInvite = async (accountId, draftMember) => {
    const normalizedEmail = normalizeEmail(draftMember.email);
    if (!normalizedEmail) throw new Error("A valid email address is required.");
    if (!isValidEmail(normalizedEmail)) throw new Error("Please enter a valid email address.");

    const plan = normalizePlan(draftMember.plan || "full");
    const features = normalizeFeatureMap(draftMember.features, plan);
    const role = ROLE_OPTIONS.includes(draftMember.role) ? draftMember.role : "staff";
    const inviteKey = emailInviteKey(normalizedEmail);
    const pendingRef = doc(db, "pendingAccountInvites", inviteKey);
    const accountInviteRef = doc(db, "accounts", accountId, "invites", inviteKey);
    const invitePayload = {
      inviteId: inviteKey,
      email: normalizedEmail,
      name: String(draftMember.name || "").trim(),
      accountId,
      workspaceUid: accountId,
      role,
      plan,
      isActive: draftMember.isActive !== false,
      features,
      status: "pending",
      planSeatType: isStaffSeatRole(role) ? "staff" : "owner",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      expiresAt: getInviteExpiryTimestamp(),
      createdBy: currentUser.uid,
      invitedBy: currentUser.uid,
      invitedByEmail: currentUser.email || "",
    };

    await setDoc(
      pendingRef,
      invitePayload,
      { merge: true }
    );
    await setDoc(accountInviteRef, invitePayload, { merge: true });
    invalidateAccountAccessCaches(accountId);

    if (typeof window !== "undefined") {
      window.localStorage.setItem("fabriquote-invite-email", normalizedEmail);
    }

    try {
      if (isDevRuntime) {
        console.log("TEAM INVITE START", {
          source: "superadmin_member_add",
          accountId,
          inviteId: inviteKey,
          email: normalizedEmail,
          role,
        });
      }
      await sendAccountInviteEmail(
        normalizedEmail,
        createAccountInviteActionCodeSettings(normalizedEmail, accountId, inviteKey),
        { accountId, workspaceUid: accountId, inviteId: inviteKey, role, invite: invitePayload }
      );
      const sentPatch = {
        status: "pending",
        inviteEmailSent: true,
        emailStatus: "sent",
        sentAt: serverTimestamp(),
        emailSentAt: serverTimestamp(),
        lastInviteSentAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      await Promise.all([
        setDoc(pendingRef, sentPatch, { merge: true }),
        setDoc(accountInviteRef, sentPatch, { merge: true }),
      ]);
    } catch (emailError) {
      const failedPatch = {
        status: "pending_email_failed",
        inviteEmailSent: false,
        emailStatus: "failed",
        emailFailureCode: emailError?.code || "",
        emailFailureMessage: emailError?.message || String(emailError || ""),
        updatedAt: serverTimestamp(),
      };
      await Promise.all([
        setDoc(pendingRef, failedPatch, { merge: true }).catch(() => {}),
        setDoc(accountInviteRef, failedPatch, { merge: true }).catch(() => {}),
      ]);
      throw emailError;
    }

    return {
      id: inviteKey,
      email: normalizedEmail,
      name: String(draftMember.name || "").trim(),
      accountId,
      role,
      plan,
      isActive: draftMember.isActive !== false,
      features,
      status: "pending",
      sentAt: new Date().toISOString(),
      lastInviteSentAt: new Date().toISOString(),
    };
  };

  const createAccount = async (event) => {
    event.preventDefault();
    if (!createDraft.name.trim()) {
      setError("Account name is required.");
      return;
    }
    const adminEmail = normalizeEmail(createDraft.adminEmail);
    if (!isValidEmail(adminEmail)) {
      setError("Initial admin email is required.");
      return;
    }

    setBusy(true);
    setNotice("");
    setError("");
    setLastSetupLink(null);

    try {
      const createWorkspaceAccountCallable = httpsCallable(functions, "createWorkspaceAccount");
      if (isDevRuntime) {
        console.log("SUPERADMIN CREATE ACCOUNT START", {
          accountName: createDraft.name.trim(),
          initialAdminEmail: adminEmail,
          plan: createDraft.plan,
          status: createDraft.isActive ? "active" : "inactive",
        });
      }
      const result = await createWorkspaceAccountCallable({
        accountName: createDraft.name.trim(),
        plan: createDraft.plan,
        status: createDraft.isActive ? "active" : "inactive",
        staffLimit: normalizeStaffLimit(createDraft.staffLimit),
        extraStaffPurchased: normalizeExtraStaffPurchased(createDraft.extraStaffPurchased),
        initialAdminEmail: adminEmail,
        onboardingMode: "send_invite",
      });
      const response = result?.data || {};
      const accountId = response.accountId || response.workspaceUid || "";
      if (!accountId) {
        throw new Error("Account was created, but the function did not return an account id.");
      }
      invalidateAccountAccessCaches(accountId);

      if (isDevRuntime) {
        console.log("SUPERADMIN CREATE ACCOUNT SUCCESS", {
          accountId,
          initialAdminUid: response.initialAdminUid || "",
          email: response.email || adminEmail,
          authUserCreated: response.authUserCreated === true,
          inviteLinkCreated: response.inviteLinkCreated === true,
          passwordSetupError: response.passwordSetupError || "",
        });
      }

      setCreateDraft({
        name: "",
        plan: "full",
        adminEmail: "",
        isActive: true,
        staffLimit: DEFAULT_STAFF_LIMIT,
        extraStaffPurchased: 0,
      });
      setSelectedId(accountId);
      if (response.setupLink || response.passwordSetupLink) {
        setLastSetupLink({
          accountId,
          email: response.email || adminEmail,
          url: response.setupLink || response.passwordSetupLink,
          source: "create_account",
        });
      }
      setNotice(
        response.inviteLinkCreated
          ? "Account created. The initial admin was linked and a password setup link is ready to copy."
          : "Account created and initial admin was linked. Password setup link could not be generated; check Firebase Auth authorized domains."
      );
      await loadAccounts();
    } catch (createError) {
      if (isDevRuntime) {
        console.error("SUPERADMIN CREATE ACCOUNT FAILED EXACT", {
          code: createError?.code,
          message: createError?.message,
          stack: createError?.stack,
          accountName: createDraft.name,
          initialAdminEmail: adminEmail,
        });
      }
      setError(getSuperAdminCallableErrorMessage(createError, "Could not create account."));
    } finally {
      setBusy(false);
    }
  };

  const saveAccount = async () => {
    if (!selectedAccount || !draft) return;
    setBusy(true);
    setNotice("");
    setError("");

    try {
      const normalizedFeatures = normalizeFeatureMap(draft.features, draft.plan);
      const updateWorkspaceActivationStatus = httpsCallable(functions, "updateWorkspaceActivationStatus");
      await updateWorkspaceActivationStatus({
        accountId: selectedAccount.id,
        billingPlan: draft.billingPlan || selectedAccount.billingPlan || "trial",
        accessPlan: draft.plan,
        accountStatus: draft.accountStatus || (draft.isActive ? "active" : "suspended"),
        trialQuotationLimit: Number(draft.trialQuotationLimit || 0) || 0,
        trialQuotationsUsed: Number(draft.trialQuotationsUsed || 0) || 0,
        manualPaymentNotes: draft.manualPaymentNotes || "",
        subscriptionValidUntil: draft.subscriptionValidUntil || null,
        lockedReason: draft.lockedReason || "",
        features: normalizedFeatures,
      });
      await updateDoc(doc(db, "accounts", selectedAccount.id), {
        name: draft.name.trim() || selectedAccount.name,
        ownerUid: draft.ownerUid || selectedAccount.ownerUid || "",
        dataOwnerUid:
          draft.dataOwnerUid ||
          draft.ownerUid ||
          selectedAccount.dataOwnerUid ||
          selectedAccount.ownerUid ||
          "",
        staffLimit: normalizeStaffLimit(draft.staffLimit),
        activeStaffCount: selectedStaffSeatUsage.active,
        extraStaffPurchased: normalizeExtraStaffPurchased(draft.extraStaffPurchased),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
      });
      invalidateAccountAccessCaches(selectedAccount.id);
      setNotice("Account access updated.");
      await loadAccounts();
      await loadActivationRequests();
      await refreshAccount();
    } catch (saveError) {
      setError(saveError?.message || "Could not update account.");
    } finally {
      setBusy(false);
    }
  };

  const sendLeadTestEmail = async () => {
    setSendingLeadTestEmail(true);
    setNotice("");
    setError("");
    try {
      const callable = httpsCallable(functions, "sendTestWebsiteLeadEmail");
      const result = await callable({});
      const response = result?.data || {};
      setNotice(
        response.emailSent
          ? `Test email sent to ${(response.recipients || []).join(", ")}.`
          : `Test email failed: ${response.error || "Unknown error"}.`
      );
    } catch (testError) {
      setError(testError?.message || "Could not send test email.");
    } finally {
      setSendingLeadTestEmail(false);
    }
  };

  const resendLeadNotification = async (leadId) => {
    if (!leadId) return;
    setResendingLeadId(leadId);
    setNotice("");
    setError("");
    try {
      const callable = httpsCallable(functions, "resendWebsiteLeadNotification");
      const result = await callable({ leadId });
      const response = result?.data || {};
      setNotice(
        response.emailSent
          ? `Lead notification resent for ${leadId}.`
          : `Lead notification retry failed for ${leadId}: ${response.error || "Unknown error"}.`
      );
      await loadWebsiteLeads();
    } catch (resendError) {
      setError(resendError?.message || "Could not resend website lead notification.");
    } finally {
      setResendingLeadId("");
    }
  };

  const addMemberToAccount = async (accountId, member) => {
    const uid = String(member.uid || "").trim();
    if (!uid) throw new Error("Member UID is required.");

    const role = member.role === "staff" ? "staff" : "owner";
    const normalizedRole = ROLE_OPTIONS.includes(member.role) ? member.role : role;
    const plan = normalizePlan(member.plan || "full");
    const features = normalizeFeatureMap(member.features, plan);
    const email = String(member.email || "").trim();
    const name = String(member.name || member.displayName || "").trim();

    await setDoc(
      doc(db, "accounts", accountId, "members", uid),
      {
        uid,
        email,
        name,
        role: normalizedRole,
        plan,
        isActive: member.isActive !== false,
        features,
        addedAt: serverTimestamp(),
        joinedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    const projectedMembers = [
      ...(membersByAccount[accountId] || []).filter((existing) => String(existing.uid || "") !== uid),
      {
        uid,
        email,
        name,
        role: normalizedRole,
        plan,
        isActive: member.isActive !== false,
        features,
      },
    ];
    await setDoc(
      doc(db, "accounts", accountId),
      {
        activeStaffCount: getStaffSeatUsage(projectedMembers, [], DEFAULT_STAFF_LIMIT).active,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    invalidateAccountAccessCaches(accountId);

    await setDoc(
      doc(db, "users", uid),
      {
        accountId,
        activeAccountId: accountId,
        role: normalizedRole,
        plan,
        isActive: member.isActive !== false,
        email,
        displayName: name,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    await setDoc(
      doc(db, "users", uid, "profile", "main"),
      {
        workspaceId: accountId,
        ownerUid: accountId,
        role: normalizedRole === "viewer" ? "staff" : normalizedRole,
        status: "active",
        email,
        name,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );
  };

  const resetMemberDraft = (overrides = {}) => {
    setMemberDraft({
      name: "",
      uid: "",
      email: "",
      role: "staff",
      plan: selectedAccount?.plan || "full",
      isActive: true,
      ...overrides,
    });
  };

  const openAddUserModal = () => {
    if (isDevRuntime) {
      console.log("SUPERADMIN ADD USER CLICKED", {
        selectedAccountId: selectedAccount?.id || "",
      });
    }
    setNotice("");
    setError("");
    resetMemberDraft();
    setIsAddUserModalOpen(true);
    if (isDevRuntime) {
      console.log("SUPERADMIN ADD USER MODAL OPENED", {
        selectedAccountId: selectedAccount?.id || "",
      });
    }
  };

  const closeAddUserModal = () => {
    if (busy) return;
    setIsAddUserModalOpen(false);
  };

  const addMember = async (event) => {
    event.preventDefault();
    if (!selectedAccount) return;
    setBusy(true);
    setNotice("");
    setError("");

    try {
      const normalizedEmail = normalizeEmail(memberDraft.email);
      const trimmedUid = String(memberDraft.uid || "").trim();
      const trimmedName = String(memberDraft.name || "").trim();
      const normalizedRole = ROLE_OPTIONS.includes(memberDraft.role)
        ? memberDraft.role
        : "staff";
      const normalizedPlan = normalizePlan(memberDraft.plan || selectedAccount.plan || "full");

      if (isDevRuntime) {
        console.log("SUPERADMIN ADD USER SUBMIT START", {
          selectedAccountId: selectedAccount.id,
          uidProvided: Boolean(trimmedUid),
          email: normalizedEmail,
          role: normalizedRole,
          plan: normalizedPlan,
        });
      }

      if (!normalizedEmail) {
        throw new Error("Please enter a valid email address.");
      }
      if (!isValidEmail(normalizedEmail)) {
        throw new Error("Please enter a valid email address.");
      }

      const existingMembers = membersByAccount[selectedAccount.id] || [];
      const existingPending = pendingInvitesByAccount[selectedAccount.id] || [];
      const consumesStaffSeat =
        isStaffSeatRole(normalizedRole) && memberDraft.isActive !== false;
      const seatUsage = getStaffSeatUsage(existingMembers, existingPending, selectedAccount.staffLimit, {
        email: normalizedEmail,
        uid: trimmedUid,
      });
      if (consumesStaffSeat && seatUsage.total >= seatUsage.limit) {
        throw new Error(STAFF_LIMIT_MESSAGE);
      }
      const duplicateAccepted = existingMembers.find(
        (member) =>
          (trimmedUid && String(member.uid || "") === trimmedUid) ||
          normalizeEmail(member.email || "") === normalizedEmail
      );
      if (duplicateAccepted) {
        throw new Error("This user already has access to the selected account.");
      }

      const duplicatePending = existingPending.find(
        (invite) => normalizeEmail(invite.email || "") === normalizedEmail
      );
      if (duplicatePending) {
        throw new Error("This email already has a pending invite for the selected account.");
      }

      const existingUser = trimmedUid
        ? { uid: trimmedUid, email: normalizedEmail }
        : await findUserByEmail(normalizedEmail);

      if (isDevRuntime) {
        console.log("SUPERADMIN ADD USER AUTH RESULT", {
          mode: existingUser?.uid ? "existing_user" : "pending_invite",
          uid: existingUser?.uid || "",
          email: normalizedEmail,
        });
      }

      if (existingUser?.uid) {
        await createPendingMemberInvite(selectedAccount.id, {
          ...memberDraft,
          name: trimmedName,
          email: normalizedEmail,
          role: normalizedRole,
          plan: normalizedPlan,
          features: normalizeFeatureMap(memberDraft.features, normalizedPlan),
        });

        await addMemberToAccount(selectedAccount.id, {
          ...memberDraft,
          uid: existingUser.uid,
          email: normalizedEmail,
          role: normalizedRole,
          plan: normalizedPlan,
          features: normalizeFeatureMap(memberDraft.features, normalizedPlan),
        });

        if (isDevRuntime) {
          console.log("SUPERADMIN ADD USER FIRESTORE WRITE RESULT", {
            mode: "existing_user",
            accountId: selectedAccount.id,
            uid: existingUser.uid,
          });
        }

        setMembersByAccount((prev) => ({
          ...prev,
          [selectedAccount.id]: [
            ...(prev[selectedAccount.id] || []),
            {
              uid: existingUser.uid,
              email: normalizedEmail,
              name: trimmedName,
              role: normalizedRole,
              plan: normalizedPlan,
              isActive: memberDraft.isActive !== false,
              features: normalizeFeatureMap(memberDraft.features, normalizedPlan),
            },
          ],
        }));
        setNotice(`Invite sent to ${normalizedEmail}. Existing user access was also assigned.`);
      } else {
        const pendingInvite = await createPendingMemberInvite(selectedAccount.id, {
          ...memberDraft,
          name: trimmedName,
          email: normalizedEmail,
          role: normalizedRole,
          plan: normalizedPlan,
          features: normalizeFeatureMap(memberDraft.features, normalizedPlan),
        });

        if (isDevRuntime) {
          console.log("SUPERADMIN ADD USER FIRESTORE WRITE RESULT", {
            mode: "pending_invite",
            accountId: selectedAccount.id,
            inviteId: pendingInvite.id,
            email: normalizedEmail,
          });
        }

        setPendingInvitesByAccount((prev) => ({
          ...prev,
          [selectedAccount.id]: [
            ...(prev[selectedAccount.id] || []).filter(
              (invite) => normalizeEmail(invite.email || "") !== normalizedEmail
            ),
            pendingInvite,
          ],
        }));
        setNotice(`Invite sent to ${normalizedEmail}. They will be attached after sign-in.`);
      }

      resetMemberDraft({ plan: selectedAccount.plan || "full" });
      setIsAddUserModalOpen(false);
      await loadAccounts();
    } catch (memberError) {
      logAddUserFailure(memberError, {
        selectedAccountId: selectedAccount?.id || "",
        attemptedEmail: memberDraft.email || "",
        attemptedUid: memberDraft.uid || "",
      });
      setError(
        memberError?.code
          ? getInviteEmailErrorMessage(memberError)
          : memberError?.message || "Could not add member."
      );
      await loadAccounts();
    } finally {
      setBusy(false);
    }
  };

  const resendPendingInvite = async (invite = {}) => {
    if (!selectedAccount) return;

    const normalizedEmail = normalizeEmail(invite.email || "");
    const inviteId = String(invite.inviteId || invite.id || emailInviteKey(normalizedEmail));
    const role = ROLE_OPTIONS.includes(invite.role) ? invite.role : "staff";

    setNotice("");
    setError("");

    if (!normalizedEmail || !inviteId) {
      setError("Could not send invite email. Invite record is missing an email or id.");
      return;
    }

    if (isDevRuntime) {
      console.log("TEAM INVITE RESEND START", {
        accountId: selectedAccount.id,
        inviteId,
        email: normalizedEmail,
        role,
      });
    }

    setResendingInviteId(inviteId);

    try {
      const accountInviteRef = doc(db, "accounts", selectedAccount.id, "invites", inviteId);
      const pendingRef = doc(db, "pendingAccountInvites", inviteId);
      const accountInviteSnap = await trackedGetDoc(accountInviteRef, {
        label: "superAdminResendAccountInviteValidate",
        cacheKey: `accounts/${selectedAccount.id}/invites/${inviteId}`,
      });
      const pendingSnap = accountInviteSnap.exists()
        ? null
        : await trackedGetDoc(pendingRef, {
            label: "superAdminResendPendingAccountInviteValidate",
            cacheKey: `pendingAccountInvites/${inviteId}`,
          });

      if (!accountInviteSnap.exists() && !pendingSnap?.exists()) {
        throw new Error("Invite record was not found. Please create the invite again.");
      }

      await sendAccountInviteEmail(
        normalizedEmail,
        createAccountInviteActionCodeSettings(normalizedEmail, selectedAccount.id, inviteId),
        {
          source: "superadmin_resend",
          accountId: selectedAccount.id,
          workspaceUid: selectedAccount.id,
          inviteId,
          invite,
          role,
        }
      );

      const resendPatch = {
        status: "pending",
        inviteEmailSent: true,
        emailStatus: "sent",
        sentAt: serverTimestamp(),
        emailSentAt: serverTimestamp(),
        resentAt: serverTimestamp(),
        lastResentAt: serverTimestamp(),
        resentBy: currentUser.uid,
        resendCount: increment(1),
        updatedAt: serverTimestamp(),
      };
      await Promise.all([
        setDoc(accountInviteRef, resendPatch, { merge: true }),
        setDoc(pendingRef, resendPatch, { merge: true }),
      ]);
      invalidateAccountAccessCaches(selectedAccount.id);

      setPendingInvitesByAccount((prev) => ({
        ...prev,
        [selectedAccount.id]: (prev[selectedAccount.id] || []).map((pendingInvite) =>
          String(pendingInvite.id || pendingInvite.inviteId || "") === inviteId
            ? {
                ...pendingInvite,
                status: "pending",
                inviteEmailSent: true,
                emailStatus: "sent",
                sentAt: new Date().toISOString(),
                resentAt: new Date().toISOString(),
                lastResentAt: new Date().toISOString(),
              }
            : pendingInvite
        ),
      }));

      if (isDevRuntime) {
        console.log("TEAM INVITE RESEND SUCCESS", {
          accountId: selectedAccount.id,
          inviteId,
          email: normalizedEmail,
          role,
        });
      }
      setNotice("Invite email sent successfully.");
    } catch (resendError) {
      if (isDevRuntime) {
        console.error("TEAM INVITE RESEND FAILED EXACT", {
          code: resendError?.code,
          message: resendError?.message,
          stack: resendError?.stack,
          accountId: selectedAccount.id,
          inviteId,
          email: normalizedEmail,
          role,
        });
      }
      setError(getInviteEmailErrorMessage(resendError));
    } finally {
      setResendingInviteId("");
    }
  };

  const sendTestInviteEmail = async () => {
    if (!isDevRuntime) return;
    const normalizedEmail = normalizeEmail(testInviteEmailValue);
    const accountId = selectedAccount?.id || "";
    const inviteId = `test_${Date.now().toString(36)}`;
    setNotice("");
    setError("");

    if (!accountId) {
      setError("Select an account before testing invite email.");
      return;
    }

    setIsTestingInviteEmail(true);
    try {
      const actionCodeSettings = createAccountInviteActionCodeSettings(
        normalizedEmail,
        accountId,
        inviteId
      );
      await sendAccountInviteEmail(normalizedEmail, actionCodeSettings, {
        source: "superadmin_direct_test",
        accountId,
        workspaceUid: accountId,
        inviteId,
        invite: {
          inviteId,
          email: normalizedEmail,
          accountId,
          status: "test_only",
        },
      });
      setNotice("Test invite email sent successfully.");
    } catch (testError) {
      if (isDevRuntime) {
        console.error("TEAM INVITE TEST EMAIL FAILED EXACT", {
          code: testError?.code,
          message: testError?.message,
          stack: testError?.stack,
          email: normalizedEmail,
          accountId,
          inviteId,
        });
      }
      setError(getInviteEmailErrorMessage(testError));
    } finally {
      setIsTestingInviteEmail(false);
    }
  };

  const copySetupLinkToClipboard = async (url = "") => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setNotice("Setup link copied.");
    } catch (copyError) {
      setError("Could not copy setup link. Please select and copy it manually.");
    }
  };

  const linkExistingAuthUser = async (event) => {
    event.preventDefault();
    if (!selectedAccount) return;
    const normalizedEmail = normalizeEmail(linkExistingDraft.email);
    if (!isValidEmail(normalizedEmail)) {
      setError("Enter the Firebase Auth user's email address.");
      return;
    }

    setLinkingExistingUser(true);
    setNotice("");
    setError("");
    setLastSetupLink(null);

    try {
      const linkExistingCallable = httpsCallable(functions, "linkExistingAuthUserToAccount");
      if (isDevRuntime) {
        console.log("SUPERADMIN LINK EXISTING AUTH USER START", {
          accountId: selectedAccount.id,
          email: normalizedEmail,
          role: linkExistingDraft.role,
          plan: linkExistingDraft.plan,
        });
      }
      const result = await linkExistingCallable({
        accountId: selectedAccount.id,
        email: normalizedEmail,
        name: linkExistingDraft.name,
        role: linkExistingDraft.role,
        plan: linkExistingDraft.plan || selectedAccount.plan || "full",
        isActive: linkExistingDraft.isActive !== false,
        createSetupLink: true,
      });
      const response = result?.data || {};
      invalidateAccountAccessCaches(selectedAccount.id);
      if (response.setupLink || response.passwordSetupLink) {
        setLastSetupLink({
          accountId: selectedAccount.id,
          email: response.email || normalizedEmail,
          url: response.setupLink || response.passwordSetupLink,
          source: "link_existing_user",
        });
      }
      setLinkExistingDraft({
        name: "",
        email: "",
        role: "admin",
        plan: selectedAccount.plan || "full",
        isActive: true,
      });
      setNotice(
        response.inviteLinkCreated
          ? "Existing Firebase Auth user linked. A password setup link is ready to copy."
          : "Existing Firebase Auth user linked. Password setup link could not be generated."
      );
      if (isDevRuntime) {
        console.log("SUPERADMIN LINK EXISTING AUTH USER SUCCESS", {
          accountId: selectedAccount.id,
          uid: response.uid || "",
          email: response.email || normalizedEmail,
          role: response.role || linkExistingDraft.role,
          inviteLinkCreated: response.inviteLinkCreated === true,
        });
      }
      await loadAccounts();
    } catch (linkError) {
      if (isDevRuntime) {
        console.error("SUPERADMIN LINK EXISTING AUTH USER FAILED EXACT", {
          code: linkError?.code,
          message: linkError?.message,
          stack: linkError?.stack,
          accountId: selectedAccount.id,
          email: normalizedEmail,
          role: linkExistingDraft.role,
        });
      }
      setError(
        getSuperAdminCallableErrorMessage(
          linkError,
          "Could not link the existing Firebase Auth user."
        )
      );
    } finally {
      setLinkingExistingUser(false);
    }
  };

  const saveEditingMember = async () => {
    if (!selectedAccount || !editingMember?.uid) return;
    setBusy(true);
    setNotice("");
    setError("");

    try {
      const plan = normalizePlan(editingMember.plan || selectedAccount.plan || "full");
      const features = normalizeFeatureMap(editingMember.features, plan);
      const normalizedRole = ROLE_OPTIONS.includes(editingMember.role)
        ? editingMember.role
        : "staff";
      const normalizedEmail = normalizeEmail(editingMember.email || "");
      const seatUsage = getStaffSeatUsage(
        membersByAccount[selectedAccount.id] || [],
        pendingInvitesByAccount[selectedAccount.id] || [],
        selectedAccount.staffLimit,
        {
          email: normalizedEmail,
          uid: editingMember.uid,
        }
      );
      if (
        editingMember.isActive === true &&
        isStaffSeatRole(normalizedRole) &&
        seatUsage.total >= seatUsage.limit
      ) {
        throw new Error(STAFF_LIMIT_MESSAGE);
      }
      await setDoc(
        doc(db, "accounts", selectedAccount.id, "members", editingMember.uid),
        {
          uid: editingMember.uid,
          email: editingMember.email || "",
          name: editingMember.name || "",
          role: normalizedRole,
          plan,
          isActive: editingMember.isActive === true,
          features,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      const projectedMembers = [
        ...(membersByAccount[selectedAccount.id] || []).filter(
          (existing) => String(existing.uid || "") !== String(editingMember.uid || "")
        ),
        {
          uid: editingMember.uid,
          email: editingMember.email || "",
          name: editingMember.name || "",
          role: normalizedRole,
          plan,
          isActive: editingMember.isActive === true,
          features,
        },
      ];
      await setDoc(
        doc(db, "accounts", selectedAccount.id),
        {
          activeStaffCount: getStaffSeatUsage(projectedMembers, [], selectedAccount.staffLimit).active,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      invalidateAccountAccessCaches(selectedAccount.id);
      await setDoc(
        doc(db, "users", editingMember.uid),
        {
          accountId: selectedAccount.id,
          activeAccountId: selectedAccount.id,
          email: editingMember.email || "",
          displayName: editingMember.name || "",
          role: normalizedRole,
          plan,
          isActive: editingMember.isActive === true,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setEditingMember(null);
      setNotice("Member access updated.");
      await loadAccounts();
      await refreshAccount();
    } catch (saveError) {
      setError(saveError?.message || "Could not update member access.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", color: "#0f172a", padding: 24 }}>
      <div style={{ maxWidth: 1440, margin: "0 auto", display: "grid", gap: 20 }}>
        <header style={{ ...cardStyle, padding: 22, display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#2563eb", textTransform: "uppercase", letterSpacing: "0.12em" }}>
              FabriQuote Platform
            </div>
            <h1 style={{ margin: "6px 0 4px", fontSize: 30, letterSpacing: "-0.04em" }}>Super Admin Panel</h1>
            <p style={{ margin: 0, color: "#64748b" }}>Create accounts, assign plans, manage feature flags, and control subscriptions.</p>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <a href="/dashboard" style={buttonStyle("secondary")}>Open App</a>
            <button type="button" onClick={() => signOut(auth)} style={buttonStyle("ghost")}>Sign Out</button>
          </div>
        </header>

        <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
          {PLAN_CARDS.map((plan) => (
            <article key={plan.code} style={{ ...cardStyle, padding: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 900, color: "#2563eb", textTransform: "uppercase" }}>{plan.label}</div>
              <p style={{ color: "#64748b", lineHeight: 1.6 }}>{plan.description}</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {FEATURE_KEYS.filter((key) => getFeatureAccess(plan.code)[key]).map((key) => (
                  <span key={key} style={chipStyle}>{FEATURE_LABELS[key] || key}</span>
                ))}
              </div>
            </article>
          ))}
        </section>

        <section style={{ display: "grid", gridTemplateColumns: "minmax(320px, 0.4fr) minmax(0, 0.6fr)", gap: 20 }}>
          <aside style={{ display: "grid", gap: 16, alignContent: "start" }}>
            <form onSubmit={createAccount} style={{ ...cardStyle, padding: 18, display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 20 }}>Create account</h2>
              <label>Account name<input style={inputStyle} value={createDraft.name} onChange={(e) => setCreateDraft((prev) => ({ ...prev, name: e.target.value }))} placeholder="Acme Furnishings" /></label>
              <label>Plan<select style={inputStyle} value={createDraft.plan} onChange={(e) => setCreateDraft((prev) => ({ ...prev, plan: e.target.value }))}>{PLAN_CARDS.map((plan) => <option key={plan.code} value={plan.code}>{plan.label}</option>)}</select></label>
              <label>Status<select style={inputStyle} value={createDraft.isActive ? "active" : "inactive"} onChange={(e) => setCreateDraft((prev) => ({ ...prev, isActive: e.target.value === "active" }))}>{STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status}</option>)}</select></label>
              <label>Staff Limit<input style={inputStyle} value={createDraft.staffLimit} onChange={(e) => setCreateDraft((prev) => ({ ...prev, staffLimit: e.target.value }))} type="number" min="0" /></label>
              <label>Extra Staff Purchased<input style={inputStyle} value={createDraft.extraStaffPurchased} onChange={(e) => setCreateDraft((prev) => ({ ...prev, extraStaffPurchased: e.target.value }))} type="number" min="0" /></label>
              <label>Initial admin email<input style={inputStyle} value={createDraft.adminEmail} onChange={(e) => setCreateDraft((prev) => ({ ...prev, adminEmail: e.target.value }))} placeholder="owner@example.com" /></label>
              <button type="submit" disabled={busy} style={buttonStyle("primary")}>Create account</button>
            </form>

            {lastSetupLink?.url ? (
              <section style={{ ...cardStyle, padding: 18, display: "grid", gap: 10, borderColor: "#bfdbfe", background: "#eff6ff" }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: 18 }}>Password setup link</h2>
                  <p style={{ margin: "4px 0 0", color: "#1e40af", fontSize: 13, lineHeight: 1.5 }}>
                    Send this setup link to {lastSetupLink.email}. It connects to account {lastSetupLink.accountId}.
                  </p>
                </div>
                <textarea
                  readOnly
                  value={lastSetupLink.url}
                  style={{ ...inputStyle, minHeight: 92, resize: "vertical", fontSize: 12 }}
                  onFocus={(event) => event.target.select()}
                />
                <button
                  type="button"
                  onClick={() => copySetupLinkToClipboard(lastSetupLink.url)}
                  style={buttonStyle("secondary")}
                >
                  Copy setup link
                </button>
              </section>
            ) : null}

            <div style={{ ...cardStyle, padding: 18, display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 20 }}>Accounts</h2>
              <input style={inputStyle} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search accounts, plans..." />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <select style={inputStyle} value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
                  <option value="all">All plans</option>
                  {PLAN_CARDS.map((plan) => <option key={plan.code} value={plan.code}>{plan.label}</option>)}
                </select>
                <select style={inputStyle} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="all">All statuses</option>
                  {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status}</option>)}
                </select>
              </div>
              <div style={{ display: "grid", gap: 8, maxHeight: 560, overflow: "auto" }}>
                {filteredAccounts.map((account) => (
                  <button
                    key={account.id}
                    type="button"
                    onClick={() => setSelectedId(account.id)}
                    style={{
                      textAlign: "left",
                      border: selectedId === account.id ? "1px solid #2563eb" : "1px solid #e2e8f0",
                      background: selectedId === account.id ? "#eff6ff" : "#ffffff",
                      borderRadius: 16,
                      padding: 14,
                      cursor: "pointer",
                    }}
                  >
                    <strong>{account.name}</strong>
                    <div style={{ color: "#64748b", fontSize: 12, marginTop: 4 }}>{account.plan} | {account.isActive ? "active" : "inactive"}</div>
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <main style={{ ...cardStyle, padding: 22 }}>
            {!selectedAccount || !draft ? (
              <p style={{ color: "#64748b" }}>Select an account to manage.</p>
            ) : (
              <div style={{ display: "grid", gap: 18 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ color: "#2563eb", fontSize: 12, fontWeight: 900, textTransform: "uppercase" }}>Account details</div>
                    <h2 style={{ margin: "4px 0 0", fontSize: 26 }}>{selectedAccount.name}</h2>
                    <p style={{ margin: "6px 0 0", color: "#64748b" }}>
                      {selectedAccount.id} | {selectedAccount.accountStatus || "active"} | {selectedAccount.billingPlan || "trial"}
                    </p>
                  </div>
                  <button type="button" disabled={busy} onClick={saveAccount} style={buttonStyle("primary")}>Save changes</button>
                </div>

                {(notice || error) && (
                  <div style={{ borderRadius: 14, padding: 12, background: error ? "#fef2f2" : "#ecfdf5", color: error ? "#b91c1c" : "#047857", fontWeight: 700 }}>
                    {error || notice}
                  </div>
                )}

                {isDevRuntime ? (
                  <section style={{ border: "1px dashed #f59e0b", borderRadius: 18, padding: 14, background: "#fffbeb", display: "grid", gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 900, color: "#b45309", textTransform: "uppercase" }}>
                        Invite email diagnostics
                      </div>
                      <p style={{ margin: "4px 0 0", color: "#92400e", fontSize: 13 }}>
                        Sends a Firebase Auth email-link invite using the same email function, without creating a staff member.
                      </p>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 1fr) auto", gap: 10 }}>
                      <input
                        style={inputStyle}
                        value={testInviteEmailValue}
                        onChange={(event) => setTestInviteEmailValue(event.target.value)}
                        placeholder="test@example.com"
                        type="email"
                      />
                      <button type="button" disabled={isTestingInviteEmail} onClick={sendTestInviteEmail} style={buttonStyle("secondary")}>
                        {isTestingInviteEmail ? "Sending..." : "Test Invite Email"}
                      </button>
                    </div>
                  </section>
                ) : null}

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                  <label>Account name<input style={inputStyle} value={draft.name} onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))} /></label>
                  <label>Owner UID<input style={inputStyle} value={draft.ownerUid || ""} onChange={(e) => setDraft((prev) => ({ ...prev, ownerUid: e.target.value }))} /></label>
                  <label>Data Owner UID<input style={inputStyle} value={draft.dataOwnerUid || ""} onChange={(e) => setDraft((prev) => ({ ...prev, dataOwnerUid: e.target.value }))} placeholder="Legacy /users/{uid} data source" /></label>
                  <label>Plan<select style={inputStyle} value={draft.plan} onChange={(e) => updateDraftPlan(e.target.value)}>{PLAN_CARDS.map((plan) => <option key={plan.code} value={plan.code}>{plan.label}</option>)}</select></label>
                  <label>Billing Plan<select style={inputStyle} value={draft.billingPlan || "trial"} onChange={(e) => setDraft((prev) => ({ ...prev, billingPlan: e.target.value }))}>{BILLING_PLAN_OPTIONS.map((plan) => <option key={plan} value={plan}>{plan}</option>)}</select></label>
                  <label>Account Status<select style={inputStyle} value={draft.accountStatus || "active"} onChange={(e) => setDraft((prev) => ({ ...prev, accountStatus: e.target.value, isActive: ["trial", "active"].includes(e.target.value) }))}>{ACCOUNT_STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status}</option>)}</select></label>
                  <label>Status<select style={inputStyle} value={draft.isActive ? "active" : "inactive"} onChange={(e) => setDraft((prev) => ({ ...prev, isActive: e.target.value === "active" }))}>{STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status}</option>)}</select></label>
                  <label>Trial Quotation Limit<input style={inputStyle} value={draft.trialQuotationLimit ?? 0} onChange={(e) => setDraft((prev) => ({ ...prev, trialQuotationLimit: e.target.value }))} type="number" min="0" /></label>
                  <label>Trial Quotations Used<input style={inputStyle} value={draft.trialQuotationsUsed ?? 0} onChange={(e) => setDraft((prev) => ({ ...prev, trialQuotationsUsed: e.target.value }))} type="number" min="0" /></label>
                  <label>Staff Limit<input style={inputStyle} value={draft.staffLimit} onChange={(e) => setDraft((prev) => ({ ...prev, staffLimit: e.target.value }))} type="number" min="0" /></label>
                  <label>Extra Staff Purchased<input style={inputStyle} value={draft.extraStaffPurchased} onChange={(e) => setDraft((prev) => ({ ...prev, extraStaffPurchased: e.target.value }))} type="number" min="0" /></label>
                  <label>Subscription valid until<input style={inputStyle} value={draft.subscriptionValidUntil || ""} onChange={(e) => setDraft((prev) => ({ ...prev, subscriptionValidUntil: e.target.value }))} placeholder="2026-12-31" /></label>
                </div>

                <div style={{ display: "grid", gap: 12 }}>
                  <label>
                    Manual payment notes
                    <textarea
                      style={{ ...inputStyle, minHeight: 84, resize: "vertical" }}
                      value={draft.manualPaymentNotes || ""}
                      onChange={(e) => setDraft((prev) => ({ ...prev, manualPaymentNotes: e.target.value }))}
                      placeholder="Collected advance, send renewal follow-up in 30 days..."
                    />
                  </label>
                  <label>
                    Locked reason
                    <input
                      style={inputStyle}
                      value={draft.lockedReason || ""}
                      onChange={(e) => setDraft((prev) => ({ ...prev, lockedReason: e.target.value }))}
                      placeholder="trial_limit_reached"
                    />
                  </label>
                </div>

                <section>
                  <h3>Feature flags</h3>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
                    {FEATURE_KEYS.map((key) => (
                      <label key={key} style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: 12, display: "flex", alignItems: "center", gap: 10 }}>
                        <input
                          type="checkbox"
                          checked={draft.features?.[key] === true}
                          onChange={(e) =>
                            setDraft((prev) => ({
                              ...prev,
                              features: {
                                ...prev.features,
                                [key]: e.target.checked,
                              },
                            }))
                          }
                        />
                        <span style={{ fontWeight: 800 }}>{FEATURE_LABELS[key] || key}</span>
                      </label>
                    ))}
                  </div>
                </section>

                <section style={{ display: "grid", gap: 12 }}>
                  <div style={{ ...cardStyle, padding: 16, borderColor: "#bfdbfe", background: "#f8fbff" }}>
                    <h3 style={{ marginTop: 0 }}>Activation requests</h3>
                    <div style={{ display: "grid", gap: 8 }}>
                      {activationRequests
                        .filter((request) => String(request.accountId || "") === String(selectedAccount.id || ""))
                        .slice(0, 5)
                        .map((request) => (
                          <div key={request.id} style={{ border: "1px solid #dbeafe", borderRadius: 14, padding: 12, background: "#ffffff" }}>
                            <strong>{request.businessName || selectedAccount.name}</strong>
                            <div style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>
                              {request.email || "No email"} | {request.phone || "No phone"} | {request.status || "new"}
                            </div>
                          </div>
                        ))}
                      {!activationRequests.some((request) => String(request.accountId || "") === String(selectedAccount.id || "")) ? (
                        <p style={{ margin: 0, color: "#64748b", fontSize: 13 }}>
                          No activation requests yet for this workspace.
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div style={{ ...cardStyle, padding: 16, borderColor: "#d1fae5", background: "#f0fdf4", display: "grid", gap: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                      <div>
                        <h3 style={{ margin: 0 }}>Website leads</h3>
                        <p style={{ margin: "4px 0 0", color: "#166534", fontSize: 13 }}>
                          Monitor public trial/demo requests and resend notifications when needed.
                        </p>
                      </div>
                      <button
                        type="button"
                        disabled={sendingLeadTestEmail}
                        onClick={sendLeadTestEmail}
                        style={buttonStyle("secondary")}
                      >
                        {sendingLeadTestEmail ? "Sending test..." : "Send Test Email"}
                      </button>
                    </div>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                          <tr style={{ textAlign: "left", color: "#475569" }}>
                            <th style={{ padding: "10px 8px" }}>Date</th>
                            <th style={{ padding: "10px 8px" }}>Type</th>
                            <th style={{ padding: "10px 8px" }}>Name</th>
                            <th style={{ padding: "10px 8px" }}>Business</th>
                            <th style={{ padding: "10px 8px" }}>Phone</th>
                            <th style={{ padding: "10px 8px" }}>Email</th>
                            <th style={{ padding: "10px 8px" }}>Email Status</th>
                            <th style={{ padding: "10px 8px" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {websiteLeads.slice(0, 20).map((lead) => (
                            <tr key={lead.id} style={{ borderTop: "1px solid #dcfce7" }}>
                              <td style={{ padding: "10px 8px", whiteSpace: "nowrap" }}>
                                {lead.createdAt?.seconds
                                  ? new Date(lead.createdAt.seconds * 1000).toLocaleString()
                                  : "-"}
                              </td>
                              <td style={{ padding: "10px 8px", textTransform: "capitalize" }}>{lead.requestType || "-"}</td>
                              <td style={{ padding: "10px 8px" }}>{lead.fullName || "-"}</td>
                              <td style={{ padding: "10px 8px" }}>{lead.businessName || "-"}</td>
                              <td style={{ padding: "10px 8px" }}>{lead.phone || "-"}</td>
                              <td style={{ padding: "10px 8px" }}>{lead.email || "-"}</td>
                              <td style={{ padding: "10px 8px" }}>
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    padding: "4px 8px",
                                    borderRadius: 999,
                                    background:
                                      lead.emailNotificationStatus === "sent"
                                        ? "#dcfce7"
                                        : lead.emailNotificationStatus === "failed"
                                          ? "#fee2e2"
                                          : "#e0f2fe",
                                    color:
                                      lead.emailNotificationStatus === "sent"
                                        ? "#166534"
                                        : lead.emailNotificationStatus === "failed"
                                          ? "#b91c1c"
                                          : "#0f766e",
                                    fontWeight: 800,
                                  }}
                                >
                                  {lead.emailNotificationStatus || "pending"}
                                </span>
                              </td>
                              <td style={{ padding: "10px 8px" }}>
                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                  <button
                                    type="button"
                                    disabled={resendingLeadId === lead.id}
                                    onClick={() => resendLeadNotification(lead.id)}
                                    style={buttonStyle("ghost")}
                                  >
                                    {resendingLeadId === lead.id ? "Resending..." : "Resend"}
                                  </button>
                                  <a
                                    href={`https://wa.me/${String(lead.phoneNormalized || "").replace(/[^\d]/g, "")}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ ...buttonStyle("ghost"), textDecoration: "none" }}
                                  >
                                    WhatsApp
                                  </a>
                                  <a
                                    href={`mailto:${lead.email || ""}`}
                                    style={{ ...buttonStyle("ghost"), textDecoration: "none" }}
                                  >
                                    Email
                                  </a>
                                </div>
                              </td>
                            </tr>
                          ))}
                          {!websiteLeads.length ? (
                            <tr>
                              <td colSpan={8} style={{ padding: "12px 8px", color: "#64748b" }}>
                                No website leads found yet.
                              </td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                    <div>
                      <h3 style={{ margin: 0 }}>Users & Access</h3>
                      <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>
                        Add an existing Firebase user by email/UID, or invite a new email into this account.
                      </p>
                      <p style={{ margin: "6px 0 0", color: selectedStaffSeatUsage.overLimit ? "#b91c1c" : "#64748b", fontSize: 13, fontWeight: 800 }}>
                        Staff seats: {selectedStaffSeatUsage.total}/{selectedStaffSeatUsage.limit}
                        {selectedStaffSeatUsage.pending ? ` (${selectedStaffSeatUsage.pending} pending)` : ""}
                      </p>
                      {selectedStaffSeatUsage.overLimit ? (
                        <p style={{ margin: "4px 0 0", color: "#b91c1c", fontSize: 13, fontWeight: 800 }}>
                          You have more staff than your current plan allows. Deactivate extra staff or remove pending invites before adding more.
                        </p>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={openAddUserModal}
                      style={buttonStyle("secondary")}
                    >
                      Add New User
                    </button>
                  </div>
                  <div style={{ display: "grid", gap: 8 }}>
                    <form
                      onSubmit={linkExistingAuthUser}
                      style={{
                        border: "1px solid #bfdbfe",
                        background: "#eff6ff",
                        borderRadius: 18,
                        padding: 14,
                        display: "grid",
                        gap: 12,
                      }}
                    >
                      <div>
                        <h4 style={{ margin: 0, fontSize: 16 }}>Link Existing Auth User to Account</h4>
                        <p style={{ margin: "4px 0 0", color: "#1e40af", fontSize: 13, lineHeight: 1.5 }}>
                          Use this for users created manually in Firebase Auth, such as a customer who can sign in but has no FabriQuote workspace access yet.
                        </p>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
                        <label>
                          Name
                          <input
                            style={inputStyle}
                            value={linkExistingDraft.name}
                            onChange={(event) =>
                              setLinkExistingDraft((prev) => ({ ...prev, name: event.target.value }))
                            }
                            placeholder="Optional display name"
                          />
                        </label>
                        <label>
                          Existing Auth email
                          <input
                            style={inputStyle}
                            value={linkExistingDraft.email}
                            onChange={(event) =>
                              setLinkExistingDraft((prev) => ({ ...prev, email: event.target.value }))
                            }
                            placeholder="vidhu17achu@gmail.com"
                            type="email"
                          />
                        </label>
                        <label>
                          Role
                          <select
                            style={inputStyle}
                            value={linkExistingDraft.role}
                            onChange={(event) =>
                              setLinkExistingDraft((prev) => ({ ...prev, role: event.target.value }))
                            }
                          >
                            {ROLE_OPTIONS.map((role) => (
                              <option key={role} value={role}>
                                {role}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Plan
                          <select
                            style={inputStyle}
                            value={linkExistingDraft.plan}
                            onChange={(event) =>
                              setLinkExistingDraft((prev) => ({ ...prev, plan: event.target.value }))
                            }
                          >
                            {PLAN_CARDS.map((plan) => (
                              <option key={plan.code} value={plan.code}>
                                {plan.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Status
                          <select
                            style={inputStyle}
                            value={linkExistingDraft.isActive ? "active" : "inactive"}
                            onChange={(event) =>
                              setLinkExistingDraft((prev) => ({
                                ...prev,
                                isActive: event.target.value === "active",
                              }))
                            }
                          >
                            {STATUS_OPTIONS.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                      <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <button
                          type="submit"
                          disabled={linkingExistingUser || busy}
                          style={buttonStyle("secondary")}
                        >
                          {linkingExistingUser ? "Linking..." : "Link Existing User"}
                        </button>
                      </div>
                    </form>

                    {(pendingInvitesByAccount[selectedAccount.id] || []).map((invite) => (
                      <div
                        key={invite.id}
                        style={{
                          border: "1px solid #fed7aa",
                          background: "#fff7ed",
                          borderRadius: 14,
                          padding: 12,
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr 220px 150px",
                          gap: 10,
                          alignItems: "center",
                        }}
                      >
                        <strong>{invite.name || "Pending user"}</strong>
                        <span style={{ color: "#9a3412" }}>{invite.email || "No email"}</span>
                        <span style={{ ...chipStyle, background: "#ffedd5", color: "#9a3412" }}>
                          pending invite | {invite.role || "staff"} | {invite.plan || selectedAccount.plan}
                        </span>
                        <button
                          type="button"
                          disabled={busy || Boolean(resendingInviteId)}
                          onClick={() => resendPendingInvite(invite)}
                          style={buttonStyle("secondary")}
                        >
                          {resendingInviteId === String(invite.inviteId || invite.id || "")
                            ? "Sending..."
                            : "Resend Invite"}
                        </button>
                      </div>
                    ))}
                    {(membersByAccount[selectedAccount.id] || []).map((member) => (
                      <div key={member.uid} style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: 12, display: "grid", gridTemplateColumns: "1fr 1fr 220px 120px", gap: 10, alignItems: "center" }}>
                        <strong>{member.name || member.uid}</strong>
                        <span style={{ color: "#64748b" }}>{member.email || "No email"}</span>
                        <span style={chipStyle}>{member.role || "owner"} | {member.plan || selectedAccount.plan} | {member.isActive === false ? "inactive" : "active"}</span>
                        <button
                          type="button"
                          style={buttonStyle("ghost")}
                          onClick={() =>
                            setEditingMember({
                              uid: member.uid,
                              name: member.name || member.displayName || "",
                              email: member.email || "",
                              role: member.role || "staff",
                              plan: normalizePlan(member.plan || selectedAccount.plan || "full"),
                              isActive: member.isActive !== false,
                              features: normalizeFeatureMap(member.features || selectedAccount.features, member.plan || selectedAccount.plan),
                            })
                          }
                        >
                          Edit
                        </button>
                      </div>
                    ))}
                  </div>
                </section>

                {editingMember ? (
                  <section style={{ border: "1px solid #bfdbfe", borderRadius: 20, padding: 18, background: "#eff6ff", display: "grid", gap: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                      <div>
                        <h3 style={{ margin: 0 }}>Edit access</h3>
                        <p style={{ margin: "4px 0 0", color: "#64748b" }}>{editingMember.uid}</p>
                      </div>
                      <button type="button" style={buttonStyle("ghost")} onClick={() => setEditingMember(null)}>Close</button>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
                      <label>Name<input style={inputStyle} value={editingMember.name || ""} onChange={(e) => setEditingMember((prev) => ({ ...prev, name: e.target.value }))} /></label>
                      <label>Email<input style={inputStyle} value={editingMember.email} onChange={(e) => setEditingMember((prev) => ({ ...prev, email: e.target.value }))} /></label>
                      <label>Role<select style={inputStyle} value={editingMember.role} onChange={(e) => setEditingMember((prev) => ({ ...prev, role: e.target.value }))}>{ROLE_OPTIONS.map((role) => <option key={role} value={role}>{role}</option>)}</select></label>
                      <label>Plan<select style={inputStyle} value={editingMember.plan} onChange={(e) => setEditingMember((prev) => ({ ...prev, plan: e.target.value, features: getFeatureAccess(e.target.value) }))}>{PLAN_CARDS.map((plan) => <option key={plan.code} value={plan.code}>{plan.label}</option>)}</select></label>
                      <label>Status<select style={inputStyle} value={editingMember.isActive ? "active" : "inactive"} onChange={(e) => setEditingMember((prev) => ({ ...prev, isActive: e.target.value === "active" }))}>{STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status}</option>)}</select></label>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
                      {FEATURE_KEYS.map((key) => (
                        <label key={key} style={{ border: "1px solid #bfdbfe", borderRadius: 14, padding: 12, display: "flex", alignItems: "center", gap: 10, background: "#ffffff" }}>
                          <input
                            type="checkbox"
                            checked={editingMember.features?.[key] === true}
                            onChange={(e) =>
                              setEditingMember((prev) => ({
                                ...prev,
                                features: {
                                  ...prev.features,
                                  [key]: e.target.checked,
                                },
                              }))
                            }
                          />
                          <span style={{ fontWeight: 800 }}>{FEATURE_LABELS[key] || key}</span>
                        </label>
                      ))}
                    </div>
                    <button type="button" disabled={busy} onClick={saveEditingMember} style={buttonStyle("primary")}>
                      Save member access
                    </button>
                  </section>
                ) : null}
              </div>
            )}
          </main>
        </section>

        {isAddUserModalOpen ? (
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 100,
              background: "rgba(15,23,42,0.48)",
              display: "grid",
              placeItems: "center",
              padding: 20,
            }}
            onClick={closeAddUserModal}
          >
            <form
              onSubmit={addMember}
              onClick={(event) => event.stopPropagation()}
              style={{
                ...cardStyle,
                width: "min(720px, 100%)",
                padding: 22,
                display: "grid",
                gap: 14,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 900, color: "#2563eb", textTransform: "uppercase" }}>
                    Superadmin user access
                  </div>
                  <h2 style={{ margin: "4px 0 0", fontSize: 24 }}>Add New User</h2>
                  <p style={{ margin: "6px 0 0", color: "#64748b", lineHeight: 1.5 }}>
                    Every added user receives a secure invite/sign-in email. Non-owner seats count against this account's configured staff limit.
                  </p>
                </div>
                <button type="button" onClick={closeAddUserModal} style={buttonStyle("ghost")}>
                  Close
                </button>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                <label>
                  Name
                  <input
                    style={inputStyle}
                    value={memberDraft.name}
                    onChange={(event) =>
                      setMemberDraft((prev) => ({ ...prev, name: event.target.value }))
                    }
                    placeholder="User name"
                  />
                </label>
                <label>
                  Email
                  <input
                    style={inputStyle}
                    value={memberDraft.email}
                    onChange={(event) =>
                      setMemberDraft((prev) => ({ ...prev, email: event.target.value }))
                    }
                    placeholder="user@example.com"
                    type="email"
                    required
                  />
                </label>
                <label>
                  Firebase UID (optional)
                  <input
                    style={inputStyle}
                    value={memberDraft.uid}
                    onChange={(event) =>
                      setMemberDraft((prev) => ({ ...prev, uid: event.target.value }))
                    }
                    placeholder="Only if known"
                  />
                </label>
                <label>
                  Role
                  <select
                    style={inputStyle}
                    value={memberDraft.role}
                    onChange={(event) =>
                      setMemberDraft((prev) => ({ ...prev, role: event.target.value }))
                    }
                  >
                    {ROLE_OPTIONS.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Plan
                  <select
                    style={inputStyle}
                    value={memberDraft.plan}
                    onChange={(event) =>
                      setMemberDraft((prev) => ({
                        ...prev,
                        plan: event.target.value,
                        features: getFeatureAccess(event.target.value),
                      }))
                    }
                  >
                    {PLAN_CARDS.map((plan) => (
                      <option key={plan.code} value={plan.code}>
                        {plan.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Status
                  <select
                    style={inputStyle}
                    value={memberDraft.isActive ? "active" : "inactive"}
                    onChange={(event) =>
                      setMemberDraft((prev) => ({
                        ...prev,
                        isActive: event.target.value === "active",
                      }))
                    }
                  >
                    {STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div style={{ borderRadius: 14, padding: 12, background: selectedStaffSeatUsage.remaining > 0 ? "#eff6ff" : "#fff7ed", color: selectedStaffSeatUsage.remaining > 0 ? "#1d4ed8" : "#9a3412", fontWeight: 800 }}>
                Staff seats used: {selectedStaffSeatUsage.total}/{selectedStaffSeatUsage.limit}
                {selectedStaffSeatUsage.remaining > 0
                  ? ` | ${selectedStaffSeatUsage.remaining} remaining`
                  : " | Limit reached for non-owner users"}
              </div>

              {error ? (
                <div style={{ borderRadius: 14, padding: 12, background: "#fef2f2", color: "#b91c1c", fontWeight: 800 }}>
                  {error}
                </div>
              ) : null}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
                <button type="button" onClick={closeAddUserModal} style={buttonStyle("ghost")}>
                  Cancel
                </button>
                <button type="submit" disabled={busy} style={buttonStyle("primary")}>
                  {busy ? "Sending invite..." : "Add User / Send Invite"}
                </button>
              </div>
            </form>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function FullScreenMessage({ title, text }) {
  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#f8fafc", padding: 24 }}>
      <div style={{ ...cardStyle, maxWidth: 520, padding: 28, textAlign: "center" }}>
        <h1>{title}</h1>
        <p style={{ color: "#64748b", lineHeight: 1.7 }}>{text}</p>
        <a href="/" style={buttonStyle("primary")}>Back to FabriQuote</a>
      </div>
    </div>
  );
}

function buttonStyle(variant) {
  const base = {
    border: "none",
    borderRadius: 999,
    padding: "11px 16px",
    fontWeight: 800,
    cursor: "pointer",
    textDecoration: "none",
    display: "inline-flex",
    justifyContent: "center",
    color: "#ffffff",
  };
  if (variant === "primary") return { ...base, background: "#2563eb" };
  if (variant === "secondary") return { ...base, background: "#0f172a" };
  return { ...base, background: "#e2e8f0", color: "#0f172a" };
}

const chipStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  padding: "5px 9px",
  fontSize: 11,
  fontWeight: 800,
  color: "#1d4ed8",
  background: "#eff6ff",
};
